import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { Component, ElementRef, HostListener, input, viewChild, viewChildren } from '@angular/core';
import {
  BbitButtonType,
  BbitCalendarDisplayStyle,
  BbitCalendarEventType,
  BbitFunction,
  BbitI18n,
  BbitObject,
  CalendarUtils,
  IBbitCalendar,
  IBbitCalendarEvent,
  IBbitCalendarNewEventOptions,
  IBbitCalendarOptions,
  IBbitParsedDay,
  IBbitParsedWeek,
} from '@bbitgmbh/bbit.utils';
import { LuxonPipe } from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { DateTime, Duration, Interval } from 'luxon';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import tinycolor from 'tinycolor2';
import { AfterAddedToDomDirective } from '../../helper/after-added-to-dom/after-added-to-dom.directive';
import { DatePickerService } from '../../helper/date-picker/date-picker.service';
import { IBbitDatePickerOverlay } from '../../helper/date-picker/interfaces';

@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
  imports: [NgIcon, NgStyle, AfterAddedToDomDirective, NgClass, AsyncPipe, LuxonPipe],
})
export class CalendarComponent {
  @HostListener('window:resize') public onResize(): void {
    this.recalculateEvents();
  }
  public readonly options = input.required<IBbitCalendarOptions>();
  public readonly dayOnly = input.required<boolean>();
  public BbitButtonType = BbitButtonType;
  public filteredEvents = new BehaviorSubject<IBbitCalendarEvent[]>([]);
  public CalendarUtils = CalendarUtils;
  readonly detailElem = viewChild.required<ElementRef>('detailElem');
  readonly datePickerButton = viewChild.required<ElementRef>('datePickerButton');
  readonly dayElements = viewChildren<ElementRef>('dayElem');
  readonly timeElements = viewChildren<ElementRef>('timeElem');
  public timeIndicatorNgStyleSubject = new BehaviorSubject<{ [key: string]: string | number }>({});
  public detailStyles = new BehaviorSubject<{ [key: string]: string }>({});
  public weekSubject = new BehaviorSubject<IBbitParsedWeek | undefined>(undefined);
  public hours = new BehaviorSubject<string[]>([]);
  public calendarUtils = CalendarUtils;
  public CalendarDisplayStyle = BbitCalendarDisplayStyle;
  public newEventHelper!: IBbitCalendarEvent;
  public scrollbarFixNgStyleSubject = new BehaviorSubject({});

  private _ngUnsubscribe = new Subject<void>();
  private _datePicker: IBbitDatePickerOverlay | undefined;

  constructor(private _datePickerService: DatePickerService) {}

  public ngOnInit(): void {
    this.newEventHelper = {
      _id: 'newEvent',
      type: BbitCalendarEventType.DEFAULT,
      designation: '',
      from: null,
      until: null,
      timezone: this.options().timezone,
      calendar: null,
      _ngStyleSubject: new BehaviorSubject({}),
      _ngClassSubject: new BehaviorSubject([]),
      // biome-ignore lint/suspicious/noExplicitAny: required
    } as any;
    this._calcHours();
    setInterval((): void => {
      this.timeIndicatorNgStyleSubject.next({
        top: `${
          Math.floor(
            DateTime.local()
              .setZone(this.options().timezone)
              .diff(DateTime.local().setZone(this.options().timezone).startOf('day'), 'minutes').minutes -
              (this.options().displayHours?.value.from || 0) * 60,
          ) + 60
        }px`,
      });
    }, 1000 * 60);
    this.options()
      .events.pipe(takeUntil(this._ngUnsubscribe))
      .subscribe((): void => {
        this.filterEvents();
      });
    this.options()
      .calendars.pipe(takeUntil(this._ngUnsubscribe))
      .subscribe((): void => {
        this.filterEvents();
      });
    this.weekSubject.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      this.filterEvents();
    });
    const options = this.options();
    if (options.displayHours) {
      options.displayHours.pipe(takeUntil(this._ngUnsubscribe)).subscribe((): void => {
        this._calcHours();
        this.filterEvents();
      });
    }

    const scrollbarWidth = `${this._getScrollbarWidth()}px`;
    this.scrollbarFixNgStyleSubject.next({
      width: scrollbarWidth,
      minWidth: scrollbarWidth,
      maxWidth: scrollbarWidth,
    });
    // biome-ignore lint/suspicious/noExplicitAny: required
    this.detailElem().nativeElement.addEventListener('scroll', (e: any): void => {
      const dayElements = this.dayElements();
      // biome-ignore lint/style/noNonNullAssertion: required
      if (!dayElements.at(-1)! || !dayElements.at(0)!) {
        return;
      }
      const totalWidth =
        // biome-ignore lint/style/noNonNullAssertion: required
        dayElements.at(-1)!.nativeElement.offsetWidth + dayElements.at(-1)!.nativeElement.offsetLeft;
      const displayWidth = this.detailElem().nativeElement.offsetWidth;
      const displayHeight = this.detailElem().nativeElement.offsetHeight;
      const totalHeight =
        ((this.options()?.displayHours?.value.to || 0) - (this.options()?.displayHours?.value.from || 0)) * 60 + 60;
      // prevent flickering on ios
      if (
        e.target.scrollTop < 0 ||
        e.target.scrollLeft < 0 ||
        e.target.scrollLeft + displayWidth > totalWidth ||
        e.target.scrollTop + displayHeight > totalHeight
      ) {
        return;
      }

      this.timeElements().forEach((elem): void => {
        elem.nativeElement.style.left = `${this.detailElem().nativeElement.scrollLeft + 5}px`;
      });
    });

    this.reload();
  }

  private _calcHours(): void {
    const options = this.options();
    if (!options?.displayHours) {
      return;
    }
    const hours = [];
    for (let i = options.displayHours.value.from; i < options.displayHours.value.to; i++) {
      hours.push(`${i}:00`);
    }
    this.detailStyles.next({
      maxHeight: `${hours.length * 61 + 61}px`,
    });
    this.hours.next(hours);
    this.timeIndicatorNgStyleSubject = new BehaviorSubject({
      top: `${
        Math.floor(
          DateTime.local()
            .setZone(options.timezone)
            .diff(DateTime.local().setZone(options.timezone).startOf('day'), 'minutes').minutes -
            options.displayHours.value.from * 60,
        ) + 60
      }px`,
    } as { [key: string]: string | number });
  }

  public ngOnChanges(): void {
    this.reload();
  }

  public debouncedFilter = BbitFunction.debounce(
    () => {
      if (!this.weekSubject?.value) {
        return;
      }
      const options = this.options();
      // biome-ignore lint/suspicious/useIterableCallbackReturn: is okay here
      options.events?.value?.map((e) => {
        e.from = e.from.setZone(this.options().timezone);
        e.until = e.until.setZone(this.options().timezone);
      });
      const filtered =
        options.events?.value?.filter((e) => {
          if (!this.weekSubject?.value) {
            return false;
          }
          return CalendarUtils.isDateInRange(e.from, this.weekSubject.value.startDate, this.weekSubject.value.endDate);
        }) || [];
      for (const event of filtered) {
        this._calculateEventStyles(event);
      }
      this.filteredEvents.next(filtered);
    },
    { wait: 100 },
  );

  public filterEvents(): void {
    this.debouncedFilter();
  }

  public ngOnDestroy(): void {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
  }

  public reload(): void {
    const week = CalendarUtils.parseWeek(this.options().date.value, { givenDayOnly: this.dayOnly() });
    this.weekSubject.next(week);
  }

  public next(): void {
    if (!this.weekSubject?.value) {
      return;
    }
    const week = CalendarUtils.getNextWeek(this.weekSubject.value);
    this.weekSubject.next(week);
    this.options().onDateChanged?.(week.startDate);
  }

  public previous(): void {
    if (!this.weekSubject?.value) {
      return;
    }
    const week = CalendarUtils.getPreviousWeek(this.weekSubject.value);
    if (week.startDate.toMillis() < DateTime.now().startOf('day').toMillis()) {
      return;
    }
    this.weekSubject.next(week);
    this.options().onDateChanged?.(week.startDate);
  }

  public setNewDate(date: DateTime): void {
    const week = CalendarUtils.parseWeek(date, { givenDayOnly: this.dayOnly() });
    this.weekSubject.next(week);
    this.options().onDateChanged?.(week.startDate);
  }

  public recalculateEvents(): void {
    if (this.filteredEvents) {
      for (const event of this.filteredEvents.value) {
        this._calculateEventStyles(event);
      }
    }
  }

  public dayClick(): void {
    const options = this.options();
    if (
      this.newEventHelper?._canBeAdded &&
      !(this.newEventHelper._hasOverlappingEvents && options.newEventOptions?.value?.preventOverlapping) &&
      !this.newEventHelper._isInPreventedPast &&
      !this.newEventHelper._isInPreventedFuture
    ) {
      options.onNewEvent?.(BbitObject.cloneDeep(this.newEventHelper));
      // this.newEventHelper._canBeAdded = false;
    }
  }

  public throttledNewEventRecalc = BbitFunction.throttle(
    (day: IBbitParsedDay, calendar: IBbitCalendar, elementRef: ElementRef, event: MouseEvent): void => {
      const options = this.options();
      if (!options?.displayHours) {
        return;
      }
      const hideNewEvent = () => {
        this.newEventHelper?._ngStyleSubject?.next({
          display: 'none',
          left: '0px',
          top: '0px',
          height: '0px',
          width: '0px',
          zIndex: -1,
        });
        if (elementRef?.nativeElement?.style) {
          elementRef.nativeElement.style.cursor = null;
        }
        return;
      };

      // get current options
      const newEventOptions: IBbitCalendarNewEventOptions = {
        ...(options.newEventOptions?.value || {}),
        ...(calendar?.newEventOptions || {}),
      };

      const defaultDuration = (newEventOptions.duration || Duration.fromObject({ hours: 1 })).shiftTo('minutes');
      const snapDuration = (newEventOptions.snapGrid || Duration.fromObject({ minutes: 15 })).shiftTo('minutes');
      const startOffset = (newEventOptions.startOffset || Duration.fromObject({ minutes: 0 })).shiftTo('minutes');

      // calculate available intervals
      const dayInterval = Interval.fromDateTimes(
        day.date
          .startOf('day')
          .plus(Duration.fromObject({ hours: options.displayHours.value.from, minutes: startOffset.minutes })),
        day.date.startOf('day').plus(Duration.fromObject({ hours: options.displayHours.value.to })),
      );
      const available = dayInterval.splitBy(snapDuration);

      interface INewEventBlock {
        interval: Interval;
        yFrom: number;
        yUntil: number;
        intersects: boolean;
      }

      const blocks: INewEventBlock[] = [];
      let lastStartMinutes = startOffset.minutes;

      for (const o of available) {
        if (!o.start) {
          continue;
        }
        const interval = Interval.fromDateTimes(o.start, o.start.plus(defaultDuration));
        // new event mus have enough "space"
        if ((interval.end || 0) > (dayInterval.end || 0)) {
          continue;
        }
        // check if it intersects with another event
        const intersects = options?.events?.value?.find((e): boolean | null => {
          return Interval.fromDateTimes(e.from, e.until).intersection(interval) && e.calendar?._id === calendar._id;
        });
        blocks.push({
          interval,
          yFrom: lastStartMinutes,
          yUntil: lastStartMinutes + defaultDuration.minutes,
          intersects: !!intersects,
        });
        lastStartMinutes += snapDuration.minutes;
      }

      // calculate positions
      let y = event.clientY - this.detailElem().nativeElement.offsetTop + this.detailElem().nativeElement.scrollTop;
      if (y < 0) {
        y = 0;
      }
      y = y - 60 + window.scrollY;

      const x = elementRef.nativeElement.offsetLeft + 2;
      const width = elementRef.nativeElement.offsetWidth - 3;

      const possibleBlocks = blocks.filter(
        (b) => y >= b.yFrom && y <= b.yUntil && (newEventOptions.preventOverlapping ? !b.intersects : true),
      );

      const currentBlock =
        possibleBlocks?.length > 0
          ? possibleBlocks.reduce((prev, curr) => (Math.abs(curr.yFrom - y) < Math.abs(prev.yFrom - y) ? curr : prev))
          : undefined;

      if (!currentBlock) {
        hideNewEvent();
        return;
      }

      if (!this.newEventHelper._canBeAdded) {
        hideNewEvent();
        return;
      }

      const from = currentBlock.interval.start;
      const until = currentBlock.interval.end;

      let fontColor = calendar?.color
        ? calendar.color
        : options.defaultEventColor
          ? options.defaultEventColor
          : 'hsl(207, 43%, 35%)';
      const color = tinycolor(fontColor);
      let background = color.isDark() ? color.lighten(70).toRgbString() : color.darken(70).toRgbString();

      if (newEventOptions.preventInPast) {
        const now = DateTime.local().setZone(options.timezone).toJSDate();
        if (
          now.getTime() >= (from || DateTime.now()).toJSDate().getTime() ||
          now.getTime() >= (until || DateTime.now()).toJSDate().getTime()
        ) {
          fontColor = 'black';
          background = 'lightgray';
          this.newEventHelper._isInPreventedPast = true;
        } else {
          this.newEventHelper._isInPreventedPast = false;
        }
      } else {
        this.newEventHelper._isInPreventedPast = false;
      }

      if (newEventOptions.preventInFutureAfter) {
        if (
          newEventOptions.preventInFutureAfter.toJSDate().getTime() <= (from || DateTime.now()).toJSDate().getTime() ||
          newEventOptions.preventInFutureAfter.toJSDate().getTime() <= (until || DateTime.now()).toJSDate().getTime()
        ) {
          fontColor = 'black';
          // color = tinycolor(fontColor);
          background = 'lightgray';
          this.newEventHelper._isInPreventedFuture = true;
        } else {
          this.newEventHelper._isInPreventedFuture = false;
        }
      } else {
        this.newEventHelper._isInPreventedFuture = false;
      }

      // update event
      let designation: string;
      if (!this.newEventHelper._originalDesignation) {
        this.newEventHelper._originalDesignation = newEventOptions.designation
          ? newEventOptions.designation
          : this.newEventHelper.designation;
      }
      if (this.newEventHelper._isInPreventedPast) {
        designation = newEventOptions.preventInPastDesignation
          ? BbitI18n.t(newEventOptions.preventInPastDesignation)
          : BbitI18n.t({ key: 'calendarPreventedPastEvent', n: 1 });
      } else if (this.newEventHelper._isInPreventedFuture) {
        designation = newEventOptions.preventInFutureAfterDesignation
          ? BbitI18n.t(newEventOptions.preventInFutureAfterDesignation)
          : BbitI18n.t({ key: 'calendarPreventedFutureEvent', n: 1 });
      } else {
        designation = `${BbitI18n.t(this.newEventHelper._originalDesignation || '')} (${calendar.designation})`;
      }
      this.newEventHelper.calendar = calendar;
      this.newEventHelper.designation = designation;
      this.newEventHelper.from = from as DateTime;
      this.newEventHelper.until = until as DateTime;

      const top =
        (from as DateTime).get('hour') * 60 -
        options.displayHours.value.from * 60 +
        (from as DateTime).get('minute') +
        1 +
        60;

      this.newEventHelper?._ngStyleSubject?.next({
        border: `1px solid ${fontColor}`,
        // position: 'absolute',
        visibility: 'visible',
        left: `${x}px`,
        top: `${top}px`,
        height: `${defaultDuration.minutes - 3}px`,
        width: `${width}px`,
        background: background,
        color: fontColor,
        zIndex: 100,
      });

      if (this.newEventHelper._isInPreventedPast && this.newEventHelper._isInPreventedFuture) {
        elementRef.nativeElement.style.cursor = 'not-allowed';
      } else {
        elementRef.nativeElement.style.cursor = 'copy';
      }
    },
    { wait: 100 },
  );

  public initializeNewEventHover(day: IBbitParsedDay, calendar: IBbitCalendar, elementRef: ElementRef): void {
    elementRef.nativeElement.addEventListener('mouseenter', (event: MouseEvent): void => {
      this.newEventHelper._canBeAdded = true;
      this.throttledNewEventRecalc(day, calendar, elementRef, event);
    });
    elementRef.nativeElement.addEventListener('mouseleave', (event: MouseEvent): void => {
      this.newEventHelper._canBeAdded = false;
      this.throttledNewEventRecalc(day, calendar, elementRef, event);
    });
    elementRef.nativeElement.addEventListener('mousemove', (event: MouseEvent): void => {
      this.throttledNewEventRecalc(day, calendar, elementRef, event);
    });
  }

  private _calculateEventStyles(event: IBbitCalendarEvent): void {
    let connectedElem: ElementRef<HTMLElement> | undefined;
    const dayElements = this.dayElements();
    if (dayElements) {
      connectedElem = dayElements.find((o): boolean => {
        const date = DateTime.fromISO(o.nativeElement.getAttribute('data-date'), { zone: 'Europe/Zurich' });
        if (this.options().displayStyle === BbitCalendarDisplayStyle.BOOKING) {
          const calendarId = o.nativeElement.getAttribute('data-calendar');
          if (!event.calendar) {
            return false;
          }
          return CalendarUtils.areDatesEqual(date, event.from) && calendarId === event.calendar._id;
        }
        return CalendarUtils.areDatesEqual(date, event.from);
      });
    }

    // check if event overlaps with another
    const options = this.options();
    const checkWithinOthers =
      options.events?.value?.filter((e): boolean => {
        if (this.options().displayStyle === BbitCalendarDisplayStyle.BOOKING) {
          if (!event.calendar || !e.calendar) {
            return false;
          }
          return e !== event && event.from >= e.from && event.from < e.until && e.calendar._id === event.calendar._id;
        }
        return e !== event && event.from >= e.from && event.from < e.until;
      }) || [];
    const withinOthersCount = checkWithinOthers.length || 0;

    const background = event.color
      ? event.color
      : event?.calendar?.color
        ? event.calendar.color
        : options.defaultEventColor
          ? options.defaultEventColor
          : 'hsl(207, 43%, 35%)';
    const color = tinycolor(background);
    const contrast = color.isDark() ? color.lighten(70).toRgbString() : color.darken(70).toRgbString();
    // biome-ignore lint/suspicious/noExplicitAny: required
    const styles: any = {
      background: background,
      border: `1px solid ${contrast}`,
      color: contrast,
      visibility: (connectedElem?.nativeElement?.offsetWidth || 0) > 0 ? 'visible' : 'hidden',
      height: `${event.until.diff(event.from, 'minutes').minutes - 1}px`,
      top: `${
        event.from.diff(event.from.startOf('day'), 'minutes').minutes -
        (options?.displayHours?.value.from || 0) * 60 +
        60
      }px`,
      width: connectedElem ? `${connectedElem.nativeElement.offsetWidth - 1 - withinOthersCount * 6}px` : '0px',
      left: connectedElem ? `${connectedElem.nativeElement.offsetLeft + withinOthersCount * 6 + 1}px` : '0px',
      zIndex: withinOthersCount + 1,
    };

    if (event.type === BbitCalendarEventType.OPENING_HOURS) {
      styles.borderRadius = '0';
      styles.border = '0';
      styles.width = connectedElem ? `${connectedElem.nativeElement.offsetWidth}px` : '0px';
      styles.left = connectedElem ? `${connectedElem.nativeElement.offsetLeft}px` : '0px';
      styles.opacity = '0.5';
      styles.zIndex = '0';
      styles.cursor = 'not-allowed';
      styles.alignItems = 'flex-end';
    }

    if (!event._ngStyleSubject) {
      event._ngStyleSubject = new BehaviorSubject(styles as { [key: string]: string | number });
    } else {
      event._ngStyleSubject.next(styles);
    }

    const classes = [];
    if (styles.height === '14px') {
      classes.push('smallest');
    }
    if (styles.height === '29px') {
      classes.push('small');
    }

    if (!event._ngClassSubject) {
      event._ngClassSubject = new BehaviorSubject(classes);
    } else {
      event._ngClassSubject.next(classes);
    }
  }

  private _getScrollbarWidth(): number {
    // Creating invisible container
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // forcing scrollbar to appear
    // biome-ignore lint/suspicious/noExplicitAny: required
    (outer.style as any).msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    document.body.appendChild(outer);

    // Creating inner element and placing it in the container
    const inner = document.createElement('div');
    outer.appendChild(inner);

    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

    // Removing temporary elements from the DOM
    outer?.parentNode?.removeChild?.(outer);

    return scrollbarWidth;
  }

  openDatePicker(): void {
    if (this._datePicker) {
      return;
    }
    this._datePicker = this._datePickerService.open({
      date: this.options().date.value,
      unsubscribe: this._ngUnsubscribe,
      element: this.datePickerButton().nativeElement,
    });
    this._datePicker.dateChanged.pipe(takeUntil(this._ngUnsubscribe)).subscribe((date) => {
      this.setNewDate(date);
    });
    this._datePicker.closed.pipe(takeUntil(this._ngUnsubscribe)).subscribe(() => {
      this._datePicker = undefined;
    });
  }
}
